import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { BrowserRouter, useSearchParams } from 'react-router-dom';
import { Bell, ClipboardList, Users as UsersIcon, User, Tag, Settings as SettingsIcon, KeyRound, LogOut, Menu, X, Zap, Calendar as CalendarIcon, Home as HomeIcon, SlidersHorizontal, Search } from 'lucide-react';
import api from './api';
import Calendar from './pages/Calendar';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import Groups from './pages/Groups';
import Notifications from './pages/Notifications';
import Interventions from './pages/Interventions';
import Categories from './pages/Categories';
import Settings from './pages/Settings';
import Login from './pages/Login';
import ChangePasswordModal from './components/ChangePasswordModal';
import { refreshPushToken, onForegroundMessage } from './firebase';
import './App.css';
import ProfilePage from './pages/ProfilePage';
import Home from './pages/Home';
import { TasksProvider } from './context/TasksContext';

function BottomNav({ page, navigate, unreadCount }) {
  const items = [
    { key: 'home', label: 'Dom', icon: HomeIcon },
    { key: 'dashboard', label: 'Zadaci', icon: ClipboardList },
    { key: 'interventions', label: 'Intervencije', icon: Zap },
    { key: 'notifications', label: 'Obavijesti', icon: Bell, badge: unreadCount },
  ];

  const activeIndex = items.findIndex(i => i.key === page);
  const indicatorLeft = activeIndex >= 0 ? `calc(${activeIndex} * 25% + 25% * 0.2)` : '0';
  const indicatorWidth = `calc(25% * 0.6)`;

  return (
    <nav style={{
      position: 'fixed', bottom: 0, left: 0, right: 0,
      background: 'white', borderTop: '1px solid #eef0f3',
      boxShadow: '0 -4px 16px rgba(0,0,0,0.12)',
      display: 'flex', zIndex: 200,
      paddingBottom: 'env(safe-area-inset-bottom)', overflow: 'hidden',
    }}>
      {activeIndex >= 0 && (
        <div style={{
          position: 'absolute', top: 0, left: indicatorLeft, width: indicatorWidth,
          height: 3, background: '#1a2a4a', borderRadius: '0 0 3px 3px',
          transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)', zIndex: 1,
        }} />
      )}
      {items.map(({ key, label, icon: Icon, badge }) => {
        const isActive = page === key;
        return (
          <button key={key} onClick={() => navigate(key)} style={{
            flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', padding: '10px 0 8px',
            background: 'none', border: 'none', cursor: 'pointer',
            position: 'relative', gap: 3, transition: 'opacity 0.15s',
          }}>
            <div style={{ position: 'relative' }}>
              <Icon size={22} color={isActive ? '#1a2a4a' : '#94a3b8'} strokeWidth={isActive ? 2.5 : 1.8} style={{ transition: 'color 0.2s' }} />
              {badge > 0 && (
                <span style={{
                  position: 'absolute', top: -5, right: -7,
                  background: '#d63031', color: 'white', borderRadius: '50%',
                  width: 16, height: 16, fontSize: 11,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700,
                }}>
                  {badge > 9 ? '9+' : badge}
                </span>
              )}
            </div>
            <span style={{
              fontSize: 12, fontWeight: isActive ? 700 : 400,
              color: isActive ? '#1a2a4a' : '#94a3b8',
              letterSpacing: '0.2px', transition: 'color 0.2s',
            }}>
              {label}
            </span>
          </button>
        );
      })}
    </nav>
  );
}

const VALID_PAGES = ['home', 'dashboard', 'interventions', 'calendar', 'notifications', 'groups', 'users', 'categories', 'settings', 'profile'];

function pageFromLocation() {
  const taskParam = new URLSearchParams(window.location.search).get('task');
  if (taskParam) return 'dashboard';
  const interventionParam = new URLSearchParams(window.location.search).get('intervention');
  if (interventionParam) return 'interventions';
  const path = window.location.pathname.replace('/', '') || 'home';
  return VALID_PAGES.includes(path) ? path : 'home';
}

function AppContent() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(pageFromLocation);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuVisible, setMenuVisible] = useState(false);
  const [openTaskId, setOpenTaskId] = useState(() => {
    const taskId = new URLSearchParams(window.location.search).get('task');
    return taskId ? parseInt(taskId) : null;
  });
  const [openInterventionId, setOpenInterventionId] = useState(() => {
    const interventionId = new URLSearchParams(window.location.search).get('intervention');
    return interventionId ? parseInt(interventionId) : null;
  });
  const [unreadCount, setUnreadCount] = useState(0);
  const [taskCountMode, setTaskCountMode] = useState('assigned');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [dashboardFilter, setDashboardFilter] = useState('all');
  const [dashboardParticipation, setDashboardParticipation] = useState(null);
  const [dashFilterRequest, setDashFilterRequest] = useState(null);   // { groups | categories, nonce } s Grupa / Kategorija
  const [interventionParticipation, setInterventionParticipation] = useState(null);
  const [dhmzFocusKey, setDhmzFocusKey] = useState(null);
  const [boardRequest, setBoardRequest] = useState(null);
  const [groupOpenRequest, setGroupOpenRequest] = useState(null);   // Profil → Grupe s otvorenom grupom   // Obavijesti → Početna s otvorenom Oglasnom pločom
  const [notifFilterRequest, setNotifFilterRequest] = useState(null);
  const [filterSummaries, setFilterSummaries] = useState({});   // { dashboard|interventions: 'Treba ljudi · 7 dana · 6' } za stisnuti navbar
  const onFilterSummary = useCallback((key, text) => setFilterSummaries(prev => (prev[key] === text ? prev : { ...prev, [key]: text })), []);   // { key, nonce } — Obavijesti otvorene s filterom
  const menuRef = useRef(null);
  const closeTimerRef = useRef(null);
  const navRafRef = useRef(null);
  const navProgressRef = useRef(0);
  const menuOpenRef = useRef(false);
  const brandRef = useRef(null);
  const compactTitleRef = useRef(null);
  const burgerRef = useRef(null);
  const searchBtnRef = useRef(null);
  // Povećalo u navbaru: stranica (Zadaci/Intervencije) registrira funkciju koja fokusira i otvori njezino polje
  const searchOpeners = useRef({});
  const registerSearch = useCallback((key, fn) => { searchOpeners.current[key] = fn; }, []);
  const [searchShown, setSearchShown] = useState({});   // { dashboard: true } dok je polje otvoreno
  const onSearchState = useCallback((key, on) => setSearchShown(prev => (!!prev[key] === on ? prev : { ...prev, [key]: on })), []);
  const visitedPages = useRef(new Set([page]));
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const applyNavOpacities = (p, mOpen, withTransition) => {
    const t = withTransition ? 'opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1)' : 'none';
    if (brandRef.current) {
      const op = mOpen ? 1 : 1 - p;
      brandRef.current.style.transition = t;
      brandRef.current.style.opacity = op;
      brandRef.current.style.pointerEvents = op < 0.15 ? 'none' : 'auto';
    }
    if (compactTitleRef.current) {
      const op = mOpen ? 0 : p;
      compactTitleRef.current.style.transition = t;
      compactTitleRef.current.style.opacity = op;
      compactTitleRef.current.style.pointerEvents = op < 0.15 ? 'none' : 'auto';
    }
    if (searchBtnRef.current) {
      const op = mOpen ? 0 : 1 - p;
      searchBtnRef.current.style.transition = t;
      searchBtnRef.current.style.opacity = op;
      searchBtnRef.current.style.pointerEvents = op < 0.15 ? 'none' : 'auto';
    }
    if (burgerRef.current) {
      const op = mOpen ? 1 : 1 - p;
      burgerRef.current.style.transition = withTransition
        ? 'opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1), transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
        : 'none';
      burgerRef.current.style.opacity = op;
      burgerRef.current.style.transform = `scale(${(0.8 + 0.2 * op).toFixed(3)})`;
      burgerRef.current.style.pointerEvents = op < 0.15 ? 'none' : 'auto';
    }
  };

  useEffect(() => {
    menuOpenRef.current = menuOpen;
    if (isMobile) applyNavOpacities(navProgressRef.current, menuOpen, true);
  }, [menuOpen, isMobile]);

  useEffect(() => {
    if (!isMobile) {
      if (menuRef.current) { menuRef.current.style.height = ''; menuRef.current.style.minHeight = ''; }
      return;
    }

    const applyFrame = (p, withTransition) => {
      const h = Math.round(64 - 20 * p) + 'px';
      if (menuRef.current) {
        menuRef.current.style.transition = withTransition
          ? 'height 0.22s cubic-bezier(0.4,0,0.2,1), min-height 0.22s cubic-bezier(0.4,0,0.2,1)'
          : 'none';
        menuRef.current.style.height = h;
        menuRef.current.style.minHeight = h;
      }
      applyNavOpacities(p, menuOpenRef.current, withTransition);
    };

    // Binary state: always fully expanded (p=0) or fully compact (p=1), never in between
    let compact = false;
    let prevScrollY = Math.max(0, window.scrollY);
    let scrollDirAccum = 0; // accumulates px in current direction; resets on direction change
    const setNavCompact = (shouldBeCompact) => {
      if (compact === shouldBeCompact) return;
      compact = shouldBeCompact;
      navProgressRef.current = shouldBeCompact ? 1 : 0;
      applyFrame(shouldBeCompact ? 1 : 0, true);
    };

    applyFrame(compact ? 1 : 0, false);
    navProgressRef.current = compact ? 1 : 0;

    const handleScroll = () => {
      if (navRafRef.current) return;
      navRafRef.current = requestAnimationFrame(() => {
        const scrollY = Math.max(0, window.scrollY);
        const delta = scrollY - prevScrollY;
        prevScrollY = scrollY;

        if (delta !== 0) {
          // Reset accumulator when direction reverses
          if ((delta > 0) !== (scrollDirAccum > 0)) scrollDirAccum = 0;
          scrollDirAccum += delta;

          if (scrollDirAccum > 8 && scrollY > 60) {
            scrollDirAccum = 0;
            setNavCompact(true);
          } else if (scrollDirAccum < -8) {
            scrollDirAccum = 0;
            setNavCompact(false);
          }
        }

        navRafRef.current = null;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (navRafRef.current) { cancelAnimationFrame(navRafRef.current); navRafRef.current = null; }
      if (menuRef.current) { menuRef.current.style.height = ''; menuRef.current.style.minHeight = ''; menuRef.current.style.transition = ''; }
    };
  }, [isMobile]);

  const openMenu = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    setMenuOpen(true);
    setMenuVisible(true);
  };

  const closeMenu = () => {
    setMenuVisible(false);
    closeTimerRef.current = setTimeout(() => setMenuOpen(false), 280);
  };

  const toggleMenu = () => menuOpen ? closeMenu() : openMenu();

  useEffect(() => {
    api.get('/me').then(res => setUser(res.data)).catch(() => setUser(null)).finally(() => setLoading(false));
    api.get('/settings/public').then(res => setTaskCountMode(res.data.task_count_mode || 'assigned')).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) return;
    const setupPush = async () => {
      try {
        if (!('Notification' in window) || !('serviceWorker' in navigator)) return;
        if (Notification.permission !== 'granted') return;
        const token = await refreshPushToken();
        if (token) { await api.post('/push/subscribe', { token }); localStorage.setItem('push_token', token); }
      } catch (err) { console.error('[PUSH SETUP ERROR]', err.message || err); }
    };
    setupPush();

    const unsubscribe = onForegroundMessage(() => {
      api.get('/notifications').then(res => setUnreadCount(res.data.filter(n => !n.is_read).length)).catch(() => {});
    });

    const handleSWMessage = (event) => {
      if (event.data?.type === 'NAVIGATE' && event.data?.url) {
        try {
          const urlObj = new URL(event.data.url);
          const taskId = urlObj.searchParams.get('task');
          if (taskId) { setOpenTaskId(parseInt(taskId)); setPage('dashboard'); }
        } catch {}
      }
    };

    if (navigator.serviceWorker) navigator.serviceWorker.addEventListener('message', handleSWMessage);

    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
      if (navigator.serviceWorker) navigator.serviceWorker.removeEventListener('message', handleSWMessage);
    };
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const fetchUnread = async () => {
      try { const res = await api.get('/notifications'); setUnreadCount(res.data.filter(n => !n.is_read).length); } catch {}
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 60000);
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    const taskId = searchParams.get('task');
    if (taskId) { setOpenTaskId(parseInt(taskId)); setPage('dashboard'); }
    const interventionId = searchParams.get('intervention');
    if (interventionId) { setOpenInterventionId(parseInt(interventionId)); setPage('interventions'); }
  }, [searchParams]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target) && !e.target.closest('.burger-btn')) {
        closeMenu();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  useEffect(() => {
    const handlePopState = () => {
      const p = pageFromLocation();
      visitedPages.current.add(p);
      setPage(p);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const logout = async () => {
    await axios.post('/auth/logout', {}, { withCredentials: true });
    window.location.href = '/';
  };

  const navigate = (p) => {
    visitedPages.current.add(p);
    setPage(p);
    window.scrollTo(0, 0);
    closeMenu();
    window.history.pushState(null, '', `/${p === 'home' ? '' : p}`);
  };

  if (loading) return <div className="loading">Učitavanje...</div>;
  if (!user) return <Login />;

  const isAdmin = user.role === 'admin';
  const iconProps = { size: 16, style: { flexShrink: 0 } };
  return (
    <div className="app">
      <nav
        ref={menuRef}
        className="navbar"
        style={{
          flexDirection: 'column', alignItems: 'stretch', padding: 0,
          overflow: 'visible',
          willChange: isMobile ? 'height' : undefined,
        }}
      >
        {/* Gornji red navbara */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 16,
          padding: '0 16px', height: '100%', minHeight: 0,
          flexShrink: 0,
        }}
          onClick={isMobile ? toggleMenu : undefined}
        >
          {/* Brand — na mobileu otvara meni, na desktopu navigira na dashboard */}
          <div
            ref={brandRef}
            onClick={e => {
              e.stopPropagation();
              if (isMobile) { toggleMenu(); } else { navigate('home'); }
            }}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
              overflow: 'hidden',
              flex: isMobile ? 1 : 'none',
            }}
          >
            <img src="/favicon.png" alt="DVD Bukovac" style={{ width: 40, height: 40, objectFit: 'contain', flexShrink: 0 }} />
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
              <span style={{ fontSize: 20, fontWeight: 700, color: 'white' }}>DVD Bukovac</span>
              <span style={{ fontSize: 11, fontWeight: 500, color: '#b2bec3', letterSpacing: '1.5px', textTransform: 'uppercase' }}>Zagreb · Osnovano 1950.</span>
            </div>
          </div>

          {/* Kompaktni naslov — uvijek u DOM-u, opacity transition bez keyframe animacije */}
          {isMobile && (
            <div ref={compactTitleRef}
              className={filterSummaries[page] ? 'nav-filter-pill' : undefined}
              onClick={filterSummaries[page] ? (e => { e.stopPropagation(); window.scrollTo({ top: 0, behavior: 'smooth' }); }) : undefined}
              style={{
                position: 'absolute', left: '50%', transform: 'translateX(-50%)',
                border: '1.5px solid white', borderRadius: 20, padding: '4px 16px',
                fontSize: 12, fontWeight: 700, color: 'white', letterSpacing: '1.5px',
                textTransform: 'uppercase', whiteSpace: 'nowrap',
                opacity: 0, pointerEvents: 'none',
              }}>
              {/* Na Zadacima/Intervencijama s uključenim filterima: sažetak filtera, dodir vraća na vrh */}
              {filterSummaries[page]
                ? <><SlidersHorizontal size={14} style={{ flexShrink: 0 }} />{filterSummaries[page]}</>
                : 'Dobrovoljno vatrogasno društvo · Bukovac'}
            </div>
          )}

          {isMobile && (page === 'dashboard' || page === 'interventions') && (
            <button ref={searchBtnRef} className={`nav-search-btn${searchShown[page] ? ' on' : ''}`} style={{ marginLeft: 'auto' }}
              aria-label={searchShown[page] ? 'Zatvori pretragu' : 'Pretraži'} aria-pressed={!!searchShown[page]}
              onMouseDown={e => e.preventDefault()}
              onClick={e => { e.stopPropagation(); searchOpeners.current[page]?.(); }}>
              <Search size={21} />
            </button>
          )}
          {isMobile && (
            <button ref={burgerRef} className="burger-btn" onClick={e => { e.stopPropagation(); toggleMenu(); }} aria-label="Izbornik"
              style={{ marginLeft: (page === 'dashboard' || page === 'interventions') ? 0 : 'auto', transformOrigin: 'center' }}>
              {menuOpen ? <X size={22} color="white" /> : <Menu size={22} color="white" />}
            </button>
          )}

          {/* Desktop menu */}
          <div className="navbar-menu desktop-menu">
            <button className={page === 'dashboard' ? 'nav-btn active' : 'nav-btn'} onClick={() => navigate('dashboard')}>Zadaci</button>
            <button className={page === 'interventions' ? 'nav-btn active' : 'nav-btn'} onClick={() => navigate('interventions')}>Intervencije</button>
            <button className={page === 'calendar' ? 'nav-btn active' : 'nav-btn'} onClick={() => navigate('calendar')}>Kalendar</button>
            <button className={page === 'groups' ? 'nav-btn active' : 'nav-btn'} onClick={() => navigate('groups')}>Grupe</button>
            <button className={page === 'users' ? 'nav-btn active' : 'nav-btn'} onClick={() => navigate('users')}>Korisnici</button>
            {isAdmin && <button className={page === 'categories' ? 'nav-btn active' : 'nav-btn'} onClick={() => navigate('categories')}>Kategorije</button>}
            {isAdmin && <button className={page === 'settings' ? 'nav-btn active' : 'nav-btn'} onClick={() => navigate('settings')}>Postavke</button>}
          </div>

          {/* Desktop user */}
          <div className="navbar-user desktop-menu">
            <div
              onClick={() => navigate('profile')}
              style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '4px 8px', borderRadius: 8, transition: 'background 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}
              title="Moj profil"
            >
              <User size={16} color="white" />
              <span className="user-name">{user.full_name || user.email}</span>
              <span className="role-badge">{user.role}</span>
            </div>
            <button onClick={logout} className="logout-btn">Odjava</button>
          </div>

          {/* Desktop zvonce */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 'auto' }}>
            {!isMobile && (
              <button onClick={() => navigate('notifications')} style={{ position: 'relative', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', display: 'flex', alignItems: 'center' }} aria-label="Obavijesti">
                <Bell size={22} color="white" />
                {unreadCount > 0 && (
                  <span style={{ position: 'absolute', top: 0, right: 0, background: '#d63031', color: 'white', borderRadius: '50%', width: 18, height: 18, fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600 }}>
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Overlay meni — lebdi ispod navbara, ne gura sadržaj */}
        {menuOpen && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 101,
            background: '#1a2a4a',
            borderTop: '1px solid rgba(255,255,255,0.1)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
            padding: '8px 0 12px',
            animation: menuVisible ? 'navMenuOpen 0.28s cubic-bezier(0.4, 0, 0.2, 1)' : 'navMenuClose 0.28s cubic-bezier(0.4, 0, 0.2, 1)',
          }}>
            <div onClick={() => navigate('profile')} style={{ padding: '8px 20px 12px', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', transition: 'background 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#0984e3', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, color: 'white', flexShrink: 0 }}>
                {(user.full_name || user.email || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
              </div>
              <div>
                <div style={{ fontWeight: 600, color: 'white', fontSize: 16 }}>{user.full_name || user.email}</div>
                <div style={{ fontSize: 12, color: '#b2bec3', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{user.role}</div>
              </div>
            </div>

            <div style={{ height: 1, background: 'rgba(255,255,255,0.08)', margin: '0 16px 8px' }} />

            {[
              { key: 'home', label: 'Dom', icon: HomeIcon },
              { key: 'dashboard', label: 'Zadaci', icon: ClipboardList },
              { key: 'interventions', label: 'Intervencije', icon: Zap },
              ...(isMobile ? [] : [{ key: 'calendar', label: 'Kalendar', icon: CalendarIcon }]),
              { key: 'groups', label: 'Grupe', icon: UsersIcon },
              { key: 'users', label: 'Korisnici', icon: User },
              ...(isAdmin ? [{ key: 'categories', label: 'Kategorije', icon: Tag }] : []),
              ...(isAdmin ? [{ key: 'settings', label: 'Postavke', icon: SettingsIcon }] : []),
            ].map(({ key, label, icon: Icon }) => (
              <button key={key} className={page === key ? 'mobile-nav-btn active' : 'mobile-nav-btn'} onClick={() => navigate(key)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px' }}>
                <Icon size={16} style={{ flexShrink: 0 }} /> {label}
              </button>
            ))}

            <div style={{ height: 1, background: 'rgba(255,255,255,0.08)', margin: '8px 16px' }} />

            <button className="mobile-nav-btn" onClick={() => { setShowChangePassword(true); closeMenu(); }} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px' }}>
              <KeyRound {...iconProps} /> Promjena lozinke
            </button>
            <button className="mobile-nav-btn logout" onClick={logout} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px' }}>
              <LogOut {...iconProps} /> Odjava
            </button>
          </div>
        )}
      </nav>

      <main className="main-content" style={{ paddingBottom: isMobile ? 'calc(88px + env(safe-area-inset-bottom))' : undefined }}>
        {visitedPages.current.has('home') && (
          <div style={{ display: page === 'home' ? undefined : 'none' }}>
            <Home user={user} taskCountMode={taskCountMode} navigate={navigate} setDashboardFilter={setDashboardFilter} setDashboardParticipation={setDashboardParticipation} setInterventionParticipation={setInterventionParticipation} dhmzFocusKey={dhmzFocusKey} onDhmzFocusConsumed={() => setDhmzFocusKey(null)} boardRequest={boardRequest} onOpenIntervention={(id) => { setOpenInterventionId(id); navigate('interventions'); }} onOpenNotifications={(key) => { setNotifFilterRequest({ key, nonce: Date.now() }); navigate('notifications'); }} />
          </div>
        )}
        {visitedPages.current.has('dashboard') && (
          <div style={{ display: page === 'dashboard' ? undefined : 'none' }}>
            <Dashboard user={user} openTaskId={openTaskId} onTaskOpened={() => setOpenTaskId(null)} taskCountMode={taskCountMode} menuOpen={menuOpen} initialFilter={dashboardFilter} initialParticipation={dashboardParticipation} onResetInitialFilters={() => { setDashboardFilter('all'); setDashboardParticipation(null); }} active={page === 'dashboard'} filterRequest={dashFilterRequest} onFilterSummary={onFilterSummary} registerSearch={registerSearch} onSearchState={onSearchState} />
          </div>
        )}
        {visitedPages.current.has('interventions') && (
          <div style={{ display: page === 'interventions' ? undefined : 'none' }}>
            <Interventions user={user} openInterventionId={openInterventionId} onInterventionOpened={() => setOpenInterventionId(null)} menuOpen={menuOpen} initialParticipation={interventionParticipation} onResetInitialFilters={() => setInterventionParticipation(null)} active={page === 'interventions'} onFilterSummary={onFilterSummary} registerSearch={registerSearch} onSearchState={onSearchState} />
          </div>
        )}
        {visitedPages.current.has('calendar') && (
          <div style={{ display: page === 'calendar' ? undefined : 'none' }}>
            <Calendar user={user} />
          </div>
        )}
        {visitedPages.current.has('notifications') && (
          <div style={{ display: page === 'notifications' ? undefined : 'none' }}>
            <Notifications onUnreadChange={setUnreadCount} user={user} navigate={navigate} onOpenDhmzWarning={setDhmzFocusKey} onOpenBoard={() => { setBoardRequest(Date.now()); navigate('home'); }} filterRequest={notifFilterRequest} onOpenIntervention={(id) => { setOpenInterventionId(id); navigate('interventions'); }} />
          </div>
        )}
        {visitedPages.current.has('groups') && (
          <div style={{ display: page === 'groups' ? undefined : 'none' }}>
            <Groups user={user} openRequest={groupOpenRequest} onShowTasks={(f) => { setDashFilterRequest({ ...f, nonce: Date.now() }); navigate('dashboard'); }} />
          </div>
        )}
        {visitedPages.current.has('users') && (
          <div style={{ display: page === 'users' ? undefined : 'none' }}>
            <Users user={user} />
          </div>
        )}
        {visitedPages.current.has('categories') && isAdmin && (
          <div style={{ display: page === 'categories' ? undefined : 'none' }}>
            <Categories onShowTasks={(f) => { setDashFilterRequest({ ...f, nonce: Date.now() }); navigate('dashboard'); }} />
          </div>
        )}
        {visitedPages.current.has('settings') && isAdmin && (
          <div style={{ display: page === 'settings' ? undefined : 'none' }}>
            <Settings />
          </div>
        )}
        {visitedPages.current.has('profile') && (
          <div style={{ display: page === 'profile' ? undefined : 'none' }}>
            <ProfilePage user={user} onUserUpdate={(updatedUser) => setUser(updatedUser)} navigate={navigate} setDashboardFilter={setDashboardFilter} setDashboardParticipation={setDashboardParticipation} setInterventionParticipation={setInterventionParticipation} onOpenGroup={(id) => { setGroupOpenRequest({ id, nonce: Date.now() }); navigate('groups'); }} onLogout={logout} />
          </div>
        )}
      </main>

      {isMobile && <BottomNav page={page} navigate={navigate} unreadCount={unreadCount} />}

      {showChangePassword && <ChangePasswordModal onClose={() => setShowChangePassword(false)} />}
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <TasksProvider>
        <AppContent />
      </TasksProvider>
    </BrowserRouter>
  );
}

export default App;
